import React, { useCallback, useMemo, useRef, useState } from "react";
import { Card, CardContent, Typography, Button, Markdown, Tooltip } from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { GetOpportunitiesDataResponse } from "@/pages/explorer/types";
import { getOpportunityStatus } from "@/pages/explorer/utils";
import { useAppState } from "@/modules/app";
import { IconChevronLeft, IconAlertTriangle } from "@tabler/icons-react";
import { DetailsTab } from "./components/DetailsTab";
import ActionsDropdown from "@/components/closingLoop/ActionsDropdown";
import { useInsightState } from "./context";
import InsightDescription from "./components/Description";
import { isExplanationContext } from "./constants";
import { CodeSQLEditor, SQLEditorRef } from "@/components/codeEditor";

interface InsightDetailsProps {
  opportunity: GetOpportunitiesDataResponse;
  savingsInfo?: GetOpportunitiesDataResponse["savings_attribution"];
  onBack: () => void;
}

export const InsightDetails: React.FC<InsightDetailsProps> = ({
  opportunity,
  savingsInfo,
  onBack,
}) => {
  const { enableOpportunityTicketCreation } = useAppState();
  const {
    startDate,
    endDate,
    extraContent,
    showActionsDropdown,
    showScheduledEventsActivity,
    sqlLineage,
    queryText,
  } = useInsightState();
  const editorRef = useRef<SQLEditorRef>(null);
  const [assignedStatus, setAssignedStatus] = useState(getOpportunityStatus(opportunity?.status));
  const [assignedOwner, setAssignedOwner] = useState(String(opportunity?.owner || ""));

  // Look up the matching tag from sqlLineage for extra fields (markdown_description, reason, parent_indices)
  const sqlLineageTag = useMemo(
    () => sqlLineage?.tags?.find(t => t.opportunity_id === opportunity.opportunity_id),
    [sqlLineage?.tags, opportunity.opportunity_id]
  );

  // Highlight the relevant code span once the Monaco editor has finished loading.
  // Called via the CodeSQLEditor onloaded callback.
  const highlightCode = useCallback(() => {
    const indices = sqlLineageTag?.parent_indices;
    if (!indices) return;
    editorRef.current?.removeHighlight();
    // parent_indices is a single span: [[startLine, startCol], [endLine, endCol]]
    editorRef.current?.setHighlight([{ index: indices }]);
    editorRef.current?.highlightSpan({ index: indices });
  }, [sqlLineageTag?.parent_indices]);

  return (
    <div className="al-space-y-4">
      <Stack align="center" justify="between">
        <Stack>
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="al-bg-bg-brand !al-p-1 !al-h-auto"
          >
            <IconChevronLeft className="al-w-4 al-h-4 al-mr-1 al-text-brand" />
          </Button>
          <Typography variant="section-heading">{opportunity.opportunity_name}</Typography>
        </Stack>
        {enableOpportunityTicketCreation && showActionsDropdown && (
          <ActionsDropdown
            opportunity={{
              opportunity_id: opportunity.opportunity_id,
              title: opportunity.opportunity_name,
              description: opportunity.description || "Query optimization opportunity",
              impact: isExplanationContext(opportunity.explanation_context)
                ? opportunity.explanation_context
                : undefined,
              savings: opportunity.money_savings?.max
                ? parseFloat(opportunity.money_savings.max)
                : opportunity.money_savings?.min
                  ? parseFloat(opportunity.money_savings.min)
                  : undefined,
              category: opportunity.money_savings ? "savings" : "performance",
              resources:
                opportunity.resources?.map(r => ({
                  resource_name: r.resource_name,
                  resource_uri: r.resource_uri,
                })) || [],
            }}
          />
        )}
      </Stack>
      <div>
        <Typography variant="body" weight="medium">
          Description
        </Typography>
        <InsightDescription opportunity={opportunity} className="!al-min-h-[0]" />
      </div>
      {sqlLineageTag && (
        <>
          <Stack justify="between" align="center">
            <Typography variant="h4" weight="medium">
              {opportunity?.opportunity_name === "potential_query_improvements"
                ? "Optimized Query"
                : "Problem"}
            </Typography>
            {opportunity?.opportunity_name !== "potential_query_improvements" &&
              !sqlLineageTag.parent_indices && (
                <Tooltip
                  title={
                    "Query mapping incomplete: The opportunity details may reference expanded " +
                    "views or temporary objects not present in the original query. Please review " +
                    "the full query execution plan for more context."
                  }
                >
                  <Stack align="center" className="icon-xs">
                    <IconAlertTriangle className="al-h-4 al-w-4" />
                  </Stack>
                </Tooltip>
              )}
          </Stack>
          <div
            className={
              "al-min-w-0 al-max-w-full al-max-h-60 al-overflow-y-auto al-break-words " +
              "al-p-2 al-border al-rounded-md " +
              "[&_*]:!al-max-w-full [&_*]:!al-break-words " +
              "[&_pre]:!al-whitespace-pre-wrap [&_pre]:!al-break-words [&_pre]:!al-max-w-full " +
              "[&_code]:!al-whitespace-pre-wrap [&_code]:!al-break-words [&_code]:!al-max-w-full"
            }
          >
            <Markdown
              content={
                sqlLineageTag.markdown_description ||
                opportunity?.description ||
                sqlLineageTag.reason ||
                ""
              }
            />
          </div>
          {queryText && sqlLineage?.is_lineage && (
            <div className="al-min-w-0 al-overflow-hidden" style={{ maxWidth: "100%" }}>
              <div style={{ maxWidth: "100%", overflow: "hidden" }}>
                <CodeSQLEditor
                  value={queryText}
                  height="28vh"
                  ref={editorRef}
                  showCopy
                  lineNumbers
                  onloaded={highlightCode}
                />
              </div>
            </div>
          )}
        </>
      )}
      {/* Tabs Section */}
      <Card className="al-border-0 !al-bg-[#F3F4F5]">
        <CardContent className="!al-pt-2 !al-p-4">
          <DetailsTab
            opportunity={opportunity}
            assignedStatus={assignedStatus}
            setAssignedStatus={setAssignedStatus}
            assignedOwner={assignedOwner}
            setAssignedOwner={setAssignedOwner}
            startDate={
              startDate instanceof Date ? startDate : startDate ? new Date(startDate) : undefined
            }
            endDate={endDate instanceof Date ? endDate : endDate ? new Date(endDate) : undefined}
            savingsInfo={savingsInfo}
            extraContent={extraContent}
            showScheduledEventsActivity={showScheduledEventsActivity}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default InsightDetails;
